import { Col, Row, Card } from 'antd';
import React from 'react';
import styled from 'styled-components';
import useUserParams from '../../shared/entitySearch/routingUtils/useUserParams';
import { useGetUserQuery } from '../../../graphql/user.generated';
import { EntityRelationship, EntityType } from '../../../types.generated';
// import UserGroups from './UserGroups';
import { RoutedTabs } from '../../shared/RoutedTabs';
import { UserAssets } from './UserAssets';
import { decodeUrn } from '../shared/utils';
import UserInfoSideBar from './UserInfoSideBar';
import { useEntityRegistry } from '../../useEntityRegistry';
import { ErrorSection } from '../../shared/error/ErrorSection';
import { useTranslation } from '../../../i18n/useTranslation';

export interface Props {
    onTabChange: (selectedTab: string) => void;
}

export enum TabType {
    Assets = 'Assets',
    Groups = 'Groups',
}
const ENABLED_TAB_TYPES = [TabType.Assets, TabType.Groups];

const GROUP_PAGE_SIZE = 20;

/**
 * Styled Components
 */
const UserProfileWrapper = styled.div``;

const Content = styled.div`
    color: #262626;
    height: 100%;

    > div {
        height: 100%;

        @media (max-width: 768px) {
            height: auto;
        }
    }

    &&& .ant-tabs > .ant-tabs-nav .ant-tabs-nav-wrap {
        + .ant-tabs-nav-operations {
            display: none;
        }
    }
`;

export const EmptyValue = styled.div`
    &:after {
        content: 'None';
        color: #b7b7b7;
        font-style: italic;
        font-weight: 100;
    }
`;

/**
 * Responsible for reading & writing users.
 */
export default function UserProfile() {
    const { t } = useTranslation();
    const { urn: encodedUrn } = useUserParams();
    const urn = decodeUrn(encodedUrn);
    const entityRegistry = useEntityRegistry();

    const { error, data, refetch } = useGetUserQuery({ variables: { urn, groupsCount: GROUP_PAGE_SIZE } });

    const castedCorpUser = data?.corpUser as any;

    const userGroups: Array<EntityRelationship> =
        castedCorpUser?.groups?.relationships.map((relationship) => relationship as EntityRelationship) || [];
    const userRoles: Array<EntityRelationship> =
        castedCorpUser?.roles?.relationships.map((relationship) => relationship as EntityRelationship) || [];

    // Routed Tabs Constants
    const getTabs = () => {
        return [
            {
                name: t(TabType.Assets),
                path: TabType.Assets.toLocaleLowerCase(),
                content: <UserAssets urn={urn} />,
                display: {
                    enabled: () => true,
                },
            },
            // {
            //     name: t(TabType.Groups),
            //     path: TabType.Groups.toLocaleLowerCase(),
            //     content: <UserGroups urn={urn} initialRelationships={userGroups} pageSize={GROUP_PAGE_SIZE} />,
            //     display: {
            //         enabled: () => userGroups?.length > 0,
            //     },
            // },
        ].filter((tab) => ENABLED_TAB_TYPES.includes(tab.name as TabType));
    };
    const defaultTabPath = getTabs() && getTabs()?.length > 0 ? getTabs()[0].path : '';
    const onTabChange = () => null;

    // Side bar data
    const sideBarData = {
        photoUrl: data?.corpUser?.editableProperties?.pictureLink
            ? `${data?.corpUser.editableProperties.pictureLink}&s=thumbnail_100`
            : undefined,
        avatarName:
            data?.corpUser?.editableProperties?.displayName ||
            data?.corpUser?.info?.displayName ||
            data?.corpUser?.info?.fullName ||
            data?.corpUser?.urn,
        name:
            data?.corpUser?.editableProperties?.displayName ||
            (data?.corpUser && entityRegistry.getDisplayName(EntityType.CorpUser, data?.corpUser)) ||
            undefined,
        role: data?.corpUser?.editableProperties?.title || data?.corpUser?.info?.title || undefined,
        team: data?.corpUser?.editableProperties?.teams?.join(',') || data?.corpUser?.info?.departmentName || undefined,
        email: data?.corpUser?.editableProperties?.email || data?.corpUser?.info?.email || undefined,
        slack: data?.corpUser?.editableProperties?.slack || undefined,
        phone: data?.corpUser?.editableProperties?.phone || undefined,
        aboutText: data?.corpUser?.editableProperties?.aboutMe || undefined,
        groupsDetails: userGroups,
        dataHubRoles: userRoles,
        urn,
    };
    return (
        <>
            {error && <ErrorSection />}
            <UserProfileWrapper>
                <Row className="f-layout-vertical-border">
                    <Col xs={24} lg={5}>
                        <Card>
                            <UserInfoSideBar sideBarData={sideBarData} refetch={refetch} />
                        </Card>
                    </Col>
                    <Col xs={24} lg={19}>
                        <Content>
                            <RoutedTabs defaultPath={defaultTabPath} tabs={getTabs()} onTabChange={onTabChange} />
                        </Content>
                    </Col>
                </Row>
            </UserProfileWrapper>
        </>
    );
}
